import {
  BaseQueryFn,
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';
const baseUrl =
  // @ts-expect-error
  import.meta.env.VITE_APP_BASE_URL || process.env.CEB_VITE_APP_BASE_URL;
const chrome = globalThis.chrome;

// Custom baseQuery that detects environment and uses appropriate method
const createCustomBaseQuery = (): BaseQueryFn => {
  // Check if we're in a Chrome extension context
  const isExtension =
    typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.id;

  if (isExtension) {
    // Chrome extension implementation using message passing
    return async (args, api, extraOptions) => {
      // Handle case where args is just a string (URL)
      let url, method, body, params, headers;

      if (typeof args === 'string') {
        url = args;
        method = 'GET';
        headers = {};
        params = undefined;
        body = undefined;
      } else {
        // Normal object args
        ({ url, method, body, params, headers = {} } = args);
      }

      // console.log('Making API request:', {
      //   baseUrl,
      //   url,
      //   method,
      //   body,
      //   params,
      // });

      if (url.includes('undefined')) {
        console.error('URL contains undefined:', url);
        return {
          error: {
            status: 'CUSTOM_ERROR',
            data: 'URL contains undefined. Please check the request parameters.',
          },
        };
      }

      // Special handling for FormData with files
      if (body instanceof FormData) {
        // Check if FormData contains any File objects
        let hasFiles = false;
        for (const [_, value] of body.entries()) {
          if (value instanceof File) {
            hasFiles = true;
            break;
          }
        }

        if (hasFiles) {
          // For FormData with files, we need to handle the fetch directly
          // in the content script since we can't pass File objects via messaging
          try {
            // Get auth token from background script
            const tokenResponse = await new Promise<{ token: string | null }>(
              (resolve) => {
                chrome.runtime.sendMessage({ type: 'GET_AUTH_TOKEN' }, resolve);
              }
            );

            // Create a headers object first
            const headersObj: Record<string, string> = {
              Accept: 'application/json',
              ...headers,
            };

            // Add authorization if token exists
            if (tokenResponse && tokenResponse.token) {
              headersObj['Authorization'] = `Bearer ${tokenResponse.token}`;
            }

            const fetchOptions: RequestInit = {
              method: method || 'GET',
              body,
              credentials: 'include' as RequestCredentials,
              headers: headersObj,
            };

            // Do the fetch directly from content script
            const response = await fetch(baseUrl + url, fetchOptions);

            if (!response.ok) {
              const errorData = await response.json();
              return { error: { status: response.status, data: errorData } };
            }

            const data = await response.json();
            return { data };
          } catch (error: any) {
            console.error('Error in content script fetch:', error);
            return {
              error: {
                status: 'FETCH_ERROR',
                data: error.message || 'Failed to fetch',
              },
            };
          }
        }
      }

      // For non-FormData requests or FormData without files
      const isFormData = body instanceof FormData;

      // Process FormData for messaging
      let processedBody = body;
      if (isFormData) {
        const formDataObj: Record<string, string | Blob> = {};
        for (const [key, value] of body.entries()) {
          formDataObj[key] = value;
        }
        processedBody = formDataObj;
      }

      return new Promise((resolve) => {
        chrome.runtime.sendMessage(
          'ekpmbeilnaahlccpgjchjjoonecikehf',
          {
            type: 'API_REQUEST',
            endpoint: baseUrl + url,
            method: method || 'GET',
            body: processedBody,
            params,
            headers: headers || {},
            credentials: 'include',
            isFormData, // Flag to indicate this is FormData
          },
          (response) => {
            // Check for runtime errors first
            if (chrome.runtime.lastError) {
              console.error('Chrome runtime error:', chrome.runtime.lastError);
              resolve({
                error: {
                  status: 'RUNTIME_ERROR',
                  data: chrome.runtime.lastError.message,
                },
              });
              return;
            }

            // Handle no response
            if (!response) {
              console.error('No response from extension');
              resolve({
                error: {
                  status: 'NO_RESPONSE',
                  data: 'No response from extension',
                },
              });
              return;
            }

            // Handle response
            if (response.error) {
              resolve({
                error: { status: 'CUSTOM_ERROR', data: response.error },
              });
            } else {
              resolve({ data: response.data });
            }
          }
        );
      });
    };
  } else {
    // Regular web app implementation using fetchBaseQuery
    return fetchBaseQuery({
      baseUrl,
      prepareHeaders: (headers, api) => {
        headers.set('Accept', 'application/json');
        // In web app, token would be handled by credentials: 'include'
        // Add auth token from Redux state if available
        const token = (api.getState() as any)?.user?.token;
        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
        }
        return headers;
      },
      credentials: 'include',
      timeout: 300000,
    });
  }
};

// Use the appropriate baseQuery based on environment
const customFetchBase = createCustomBaseQuery();

export const baseApi = createApi({
  baseQuery: customFetchBase,
  tagTypes: [
    'Site',
    'Task',
    'Attribute',
    'Filter',
    'Pages',
    'CollaborateTasks',
    'Snippet',
    'Contributor',
    'Setting',
    'Project',
    'Site',
    'InvitedUsers',
    'User',
    'UpgradePlans',
    'Workspace',
    'WorkspaceSite',
    'Milestone',
    'AiDesignReviewIssues',
    'AiSettings',
    'Permission',
  ],
  endpoints: () => ({}),
});
